import { Component, inject, OnInit, signal } from '@angular/core';
import {
  OperationDetailService,
  OperationService,
  PaymentTypePipe,
  TransactionService,
  TransactionTypePipe,
  VisitComment,
  VisitCommentService,
} from '@beautyapp/core';
import { openPackagePurchaseDrawer } from '@beautyapp/service-packages';
import { Observable, share } from 'rxjs';
import { tap } from 'rxjs/operators';
import { NZ_DRAWER_DATA, NzDrawerService } from 'ng-zorro-antd/drawer';
import { UserViewComponent } from '@beautyapp/users';
import { EmployeeViewComponent } from '@beautyapp/hr';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzDescriptionsModule } from 'ng-zorro-antd/descriptions';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NgxPermissionsModule } from 'ngx-permissions';
import { NzButtonComponent } from 'ng-zorro-antd/button';
import { NzPopconfirmDirective } from 'ng-zorro-antd/popconfirm';
import { NzIconDirective } from 'ng-zorro-antd/icon';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzAlertComponent } from 'ng-zorro-antd/alert';
import { NzTimelineModule } from 'ng-zorro-antd/timeline';
import { NzPopoverModule } from 'ng-zorro-antd/popover';

@Component({
  selector: 'webapp-operation-detail',
  templateUrl: './operation-detail.component.html',
  styleUrls: ['./operation-detail.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    NzCardModule,
    NzDescriptionsModule,
    NzDividerModule,
    NzTagModule,
    NgxPermissionsModule,
    NzButtonComponent,
    NzPopconfirmDirective,
    NzIconDirective,
    TransactionTypePipe,
    PaymentTypePipe,
    NzTableModule,
    NzAlertComponent,
    NzTimelineModule,
    NzPopoverModule,
  ],
})
export class OperationDetailComponent implements OnInit {
  private operationService = inject(OperationService);
  private operationDetailService = inject(OperationDetailService);
  private transactionService = inject(TransactionService);
  private visitCommentService = inject(VisitCommentService);
  private drawerService = inject(NzDrawerService);

  operationId = inject(NZ_DRAWER_DATA).operationId as number;

  operation$!: Observable<any>;
  operationDetails$!: Observable<any>;
  comments = signal<VisitComment[]>([]);
  readonly packageItems = signal<any[]>([]);
  readonly packagePurchaseId = signal<string | null>(null);

  ngOnInit(): void {
    this.getOperation();
  }

  getOperation() {
    this.operation$ = this.operationService.getById(this.operationId).pipe(
      tap((operation: any) => {
        if (operation?.visitId && operation?.operationSource === 'BOOKING') {
          this.visitCommentService
            .getComments(operation.visitId)
            .subscribe((allComments) => {
              this.comments.set(
                allComments.filter(
                  (c: VisitComment) => c.bookingId === operation.bookingId
                )
              );
            });
        }
        if (operation?.operationSource === 'SERVICE_PACKAGE') {
          this.packagePurchaseId.set(
            (operation as any)?.servicePackagePurchaseId ?? null
          );
        }
      }),
      share()
    );
    this.operationDetails$ = this.operationDetailService
      .getByOperation(this.operationId)
      .pipe(share());
    this.operationDetails$.subscribe((rows) => {
      this.packageItems.set(
        (rows ?? []).filter((r: any) => r.operationType === 'PACKAGE_ITEM')
      );
    });
  }

  getSourceColor(source: string): string {
    const colors: Record<string, string> = {
      BOOKING: 'blue',
      REALIZATION: 'green',
      CASHIER: 'gold',
      SALARY: 'purple',
      EXPENCE: 'red',
      PURCHASE: 'cyan',
      SERVICE_PACKAGE: 'geekblue',
    };
    return colors[source] || 'default';
  }

  getProducts(operationDetails: any[]) {
    return operationDetails.filter(
      (f: any) =>
        f.operationType === 'SELL' ||
        f.operationType === 'SPEND' ||
        f.operationType === 'BUY'
    );
  }

  getSalary(operationDetails: any[]) {
    return operationDetails.filter((f: any) => f.operationType === 'SALARY');
  }

  getEvents(operationDetails: any[]) {
    return operationDetails.filter((f: any) => f.operationType === 'EVENT');
  }

  getSpecEvents(operationDetails: any[]) {
    return operationDetails.filter(
      (f: any) => f.operationType === 'EVENT_SPEC'
    );
  }

  getSalaryOut(operationDetails: any[]) {
    return operationDetails.filter(
      (f: any) => f.operationType === 'SALARY_OUT'
    );
  }

  getBalance(operationDetails: any[]) {
    return operationDetails.filter(
      (f: any) =>
        f.operationType === 'BALANCEIN' || f.operationType === 'BALANCEOUT'
    );
  }

  getExpence(operationDetails: any) {
    return operationDetails.filter((f: any) => f.operationType === 'EXPENCE');
  }

  getCashierAdd(operationDetails: any) {
    return operationDetails.filter(
      (f: any) => f.operationType === 'CASHIER_ADD'
    );
  }

  openPackage(purchaseId?: string | null): void {
    const pid = purchaseId ?? this.packagePurchaseId();
    if (pid) {
      openPackagePurchaseDrawer(this.drawerService, pid);
    }
  }

  viewUser(user: any) {
    let title = `${user.firstname} ${user.lastname || ''}`;
    if (user.cardNo) {
      title += ` - # ${user.cardNo}`;
    }
    this.drawerService.create<UserViewComponent, { data: string }, string>({
      nzTitle: title,
      nzContent: UserViewComponent,
      nzWrapClassName: 'user-drawer',
      nzBodyStyle: { padding: '0 15px 15px 15px' },
      nzData: { data: user.id },
    });
  }

  viewEmployee(employee: any) {
    this.drawerService.create<
      EmployeeViewComponent,
      { employeeId: string },
      string
    >({
      nzTitle: employee?.user?.fullName,
      nzContent: EmployeeViewComponent,
      nzWrapClassName: 'user-drawer',
      nzBodyStyle: { padding: '0 15px 15px 15px' },
      nzData: { employeeId: employee.id },
    });
  }

  delete(id: string) {
    this.transactionService.delete(id).subscribe(() => {
      this.getOperation();
    });
  }
}
